import React, { useContext, useState, useEffect, useReducer, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Body, Box, Button, Card, CollectionFilters, Pagination, SearchResultsGrid, TwoColumn, breakpoints, red, grey400, grey600 } from 'dma-ui-kit';
import axios from 'axios';
import styled from 'styled-components';
import SiteContext from 'site/SiteContext';
import { translateInterfaceText } from 'helpers/translation';
import { filterQuery, history, unpackFilterQuery } from 'helpers/search';
import { buildIIIFImageURL } from 'helpers/collection';
import ImageNotFound from 'assets/missing.png';
import { reducer as queryReducer } from 'search/queries';
import { ConditionalWrap } from 'helpers/ConditionalWrap';
import useBreakpoint from 'use-breakpoint';

const InputContainer = styled(Box)`
  display: flex;
  padding: 10px 0;
  align-items: baseline;
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${grey400};
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 2002;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SuggestionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${grey400};
  transition: background-color 0.2s ease;
  background-color: ${props => props.$active ? grey600 : 'transparent'};
  
  &:hover {
    background-color: ${grey600};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionText = styled.div`
  font-size: 1.2rem;
  font-weight: 400;
  color: ${red};
  margin-bottom: 4px;
`;

const Input = styled.input`
  font-family: 'usual';
  font-weight: 400;
  font-size: 2rem;
  width: 100%;
  font-style: italic;
  margin-right: 20px;
  box-sizing: border-box;
  padding: 10px;
  color: ${red};
  background-color: transparent;
  border: none;
  border-bottom: 4px solid ${red};

  &:focus {
    outline: none;
    border-bottom: 4px solid ${red};
  }

  ::placeholder {
    font-style: none;
    color: ${grey400};
  }
`;

const Container = styled(Box)`
  padding: 0 12px;

  @media (min-width: 1025px) {
      padding: 0 48px;
  }
  @media (max-width: 1024px) {
      padding: 0 24px;
  }    

  margin-bottom: 10px;
  display: block;
  position: relative;
`;

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  width: 204px;
  padding-left: 0;
  position: relative;
`;

const PaginationContainer = styled(Box)`
  max-width: 320px;
  margin: auto;
  margin-top: 48px;

  @media (min-width: 1025px) {
      max-width: 800px;
  }
  @media (max-width: 1024px) {
      max-width: 640px;
  }    
`;

function compareKeys(a, b) {
  return (a.key < b.key) ? -1 : (a.key > b.key) ? 1 : 0;
}

const rowMapping = {
  mobile: 2,
  tabletSmall: 3,
  tablet: 3,
  desktop: 3,
  desktopLarge: 4
};

// Slider lower bound is floored to this many years so the track starts on a round number.
const DATE_BOUND_ROUNDING = 50;

const parseYear = value => {
  if (value === undefined || value === null || value === '') return null;
  const next = Number(value);
  return Number.isFinite(next) ? Math.trunc(next) : null;
};

const formatYear = bcLabel => year => (year < 0 ? `${Math.abs(year)} ${bcLabel}` : String(year));

// Holdings grow roughly exponentially toward the present, so the date track is
// non-linear: antiquity gets a small share at century steps, the modern era
// most of the track at single years. Typed values are never snapped.
const DATE_SCALE = {
  from: -3000,
  segments: [
    { step: 100, to: 0, weight: 15 },
    { step: 25, to: 1500, weight: 20 },
    { step: 5, to: 1900, weight: 30 },
    { step: 1, to: 2100, weight: 35 }
  ]
};

// Accepts "1850", "-500", "500 BC", "500 BCE", "1850 AD", "1850 CE" and the
// translated BC label, case-insensitively. Anything else is invalid (null).
const parseYearInput = bcLabel => text => {
  const match = String(text).trim().match(/^(-?\d+)\s*([A-Za-z.]*)$/);
  if (!match) return null;
  const year = Number(match[1]);
  if (!Number.isFinite(year)) return null;
  const era = (match[2] || '').replace(/\./g, '').toUpperCase();
  const bc = bcLabel.toUpperCase();
  if (era && !['BC', 'BCE', 'AD', 'CE', bc].includes(era)) return null;
  const isBC = era === 'BC' || era === 'BCE' || (era !== '' && era === bc);
  return isBC ? -Math.abs(year) : year;
};

const CollectionSearch = React.memo(() => {

  const { displayLanguage } = useContext(SiteContext);
  const searchEndpoint = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/collection`;
  const suggestURL = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/collection/suggest`;
        
    const noResultsText = translateInterfaceText("No Results Found");
    const loadingText = translateInterfaceText("Loading");
    const searchText = translateInterfaceText('Search');
    const enterSearchTermText = translateInterfaceText('Enter search term');

    const artistText = translateInterfaceText("Artist");
    const cultureText = translateInterfaceText("Culture");
    const departmentText = translateInterfaceText("Department");
    const mediumText = translateInterfaceText("Medium");
    const onViewText = translateInterfaceText("On View");
    const dateText = translateInterfaceText("Date");
    const clearText = translateInterfaceText("Clear");
    const bcText = translateInterfaceText("BC");

    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const initialTerm = params.get('s');
    const initialFilter = unpackFilterQuery(params.get('f'));
    const initialPage = params.get('p');

    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(false);

    const debounceTimeoutRef = useRef(null);
    const suggestionsRef = useRef(null);

    const [options, dispatch] = useReducer(
      queryReducer,
      {  
        type: 'collection',
        page: initialPage || 1,
        term: initialTerm || '',
        limit: 20,
        filters: initialFilter || {}
      },  
    );

    useEffect(() => {
      let active = true;

      const fetchResults = async () => {
        setLoading(true);

        try {
          const queryParams = new URLSearchParams();
          const pageLimit = options.limit || 20;
          const currentPage = options.page || 1;

          queryParams.append('size', pageLimit);
          queryParams.append('from', (currentPage - 1) * pageLimit);

          if (options.term) {
            queryParams.append('q', options.term);
          }

          if (options.filters && Object.keys(options.filters).length > 0) {
            queryParams.append('filters', JSON.stringify(options.filters));
          }

          if (displayLanguage && displayLanguage !== 'en') {
            queryParams.append('l', displayLanguage);
          }

          const response = await axios.get(`${searchEndpoint}?${queryParams.toString()}`);

          if (!active) return;

          setResults({
            hits: response.data.hits || response.data.results || [],
            resultCount: response.data.total || response.data.pagination?.total || 0,
            pageCount: response.data.pageCount || response.data.pagination?.totalPages || 1,
            currentPage: response.data.currentPage || response.data.pagination?.currentPage || currentPage,
            aggregations: response.data.aggregations || {},
          });
        } catch (error) {
          if (active) {
            console.error('Error fetching collection search results:', error);
            setResults({ hits: [], aggregations: {}, pageCount: 1, currentPage: 1, resultCount: 0 });
          }
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

      fetchResults();

      return () => {
        active = false;
      };
    }, [displayLanguage, options, searchEndpoint]);

    // Fetch suggestions from API
    const fetchSuggestions = useCallback(async (term) => {
      if (!term || term.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        const response = await fetch(`${suggestURL}?q=${encodeURIComponent(term)}`);
        if (response.ok) {
          const data = await response.json();
          if (data.suggestions && Array.isArray(data.suggestions)) {
            setSuggestions(data.suggestions);
            setActiveIndex(-1);
            setShowSuggestions(true);
          }
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    }, [suggestURL]);

    // Debounced suggestion fetching
    const debouncedFetchSuggestions = useCallback((term) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      debounceTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(term);
      }, 300);
    }, [fetchSuggestions]);

    // Handle clicks outside suggestions dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    const handleFilterChange = ( {filter, item} ) => {
      const { filters } = options;
      const payload = filters ? { ...filters } : { };

      // Delete the value if it matches current, otherwise overlay
      if (payload[filter] && payload[filter] == item)
        delete payload[filter];
      else
        payload[filter] = [ item ];
      
      dispatch({ type: 'setFilters', payload });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/collection/search?s=${options.term}&f=${filterQuery(payload)}&p=1`);
      window.scrollTo(0, 0);
    };

    // Date slider released (or cleared). `range` is [fromYear, toYear] or null.
    const handleRangeChange = ({ filter, range }) => {
      const { filters } = options;
      const payload = filters ? { ...filters } : { };

      if (!range)
        delete payload[filter];
      else
        payload[filter] = [ String(range[0]), String(range[1]) ];

      dispatch({ type: 'setFilters', payload });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/collection/search?s=${options.term}&f=${filterQuery(payload)}&p=1`);
      window.scrollTo(0, 0);
    };

    const handleTermChange = e => {
      const value = e.target.value;
      setSearchTerm(value);
      setActiveIndex(-1);
      debouncedFetchSuggestions(value);
    };

    const handleKeyDown = (e) => {
      const open = showSuggestions && suggestions.length > 0;

      if (e.key === 'ArrowDown' && open) {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp' && open) {
        e.preventDefault();
        setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (open && activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSuggestionClick(suggestions[activeIndex]);
        } else {
          handleTermSubmit();
        }
        setShowSuggestions(false);
        setActiveIndex(-1);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    };

    const handleSuggestionClick = (suggestion) => {
      setSearchTerm(suggestion);
      setShowSuggestions(false);
      handleTermSubmit(suggestion);
    };

    const handleTermSubmit = (val) => {
      const term = typeof val === 'string' ? val : searchTerm;
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
      dispatch({ type: 'setTerm', payload: term });
      dispatch({ type: 'setFilters', payload: {} });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/collection/search?s=${term}&f=${filterQuery({})}&p=1`);
      window.scrollTo(0, 0);
    };

    const handlePageChange = val => {
      dispatch({ type: 'setPagination', payload: { page: val, limit: options.limit }});
      history.push(`/art/collection/search?s=${options.term}&f=${filterQuery(options.filters)}&p=${val}`);
      window.scrollTo(0, 0);
    };

    const { breakpoint } = useBreakpoint(breakpoints);
    
    return (
      <Container>
        <Box mb="24px" style={{position: 'relative', zIndex: 10}}>
          <InputContainer ref={suggestionsRef}>
            <Input
              onChange={handleTermChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (suggestions.length > 0 && searchTerm.length >= 2) {
                  setShowSuggestions(true);
                }
              }}
              type={'text'}
              value={searchTerm}
              placeholder={options?.term ? options.term : enterSearchTermText}
            />
            <Box>
              <Button
                variant="outline"
                label={searchText}
                onClick={handleTermSubmit}
                compact={true}
                color={red}
              />
            </Box>
            {showSuggestions && suggestions.length > 0 && (
              <SuggestionsDropdown>
                {suggestions.map((suggestion, index) => (
                  <SuggestionItem
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setActiveIndex(index)}
                    $active={index === activeIndex}
                  >
                    <SuggestionText>{suggestion}</SuggestionText>
                  </SuggestionItem>
                ))}
              </SuggestionsDropdown>
            )}
          </InputContainer>
        </Box>
        
        {(() => {
          // Date slider: bounds come from the API (computed without the date
          // filter so they stay stable while the user narrows the range).
          let dateFilter = null;
          const dateMin = parseYear(results?.aggregations?.date?.min);
          const dateMax = parseYear(results?.aggregations?.date?.max);
          if (dateMin !== null && dateMax !== null && dateMin < dateMax) {
            const sliderMin = Math.floor(dateMin / DATE_BOUND_ROUNDING) * DATE_BOUND_ROUNDING;
            const sliderMax = dateMax;
            const selectedFrom = parseYear(options.filters.date?.[0]);
            const selectedTo = parseYear(options.filters.date?.[1]);

            dateFilter = {
              clearLabel: clearText,
              formatValue: formatYear(bcText),
              label: dateText,
              max: sliderMax,
              maxLabel: `${dateText}: to`,
              min: sliderMin,
              minLabel: `${dateText}: from`,
              parseValue: parseYearInput(bcText),
              scale: DATE_SCALE,
              step: 1,
              type: 'range',
              value: [
                Math.max(selectedFrom ?? sliderMin, sliderMin),
                Math.min(selectedTo ?? sliderMax, sliderMax)
              ]
            };
          }

          // Key order here is the render order of the filter panel.
          const filters = {
            artist: { label: artistText, options: [] },
            culture: { label: cultureText, options: [] },
            ...(dateFilter ? { date: dateFilter } : {}),
            department: { label: departmentText, options: [] },
            medium: { label: mediumText, options: [] },
            onview: { label: onViewText, options: [] }
          };

          filters.onview.options['On View'] = {
            count: (results?.aggregations?.onview?.doc_count) ? results.aggregations.onview.doc_count : 0,
            selected: ((options.filters.onview == 'On View') ? true : false)
          };

          if (results?.aggregations?.artist?.name)
            results.aggregations.artist.name.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.artist.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.artist == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.culture)
            results.aggregations.culture.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.culture.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.culture == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.department)
            results.aggregations.department.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.department.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.department == agg.key) ? true : false)
                };
            });

          if (results?.aggregations?.medium)
            results.aggregations.medium.sort(compareKeys).map(agg => {
              if (agg.key.length > 0)
                filters.medium.options[agg.key] = {
                  count: agg.doc_count,
                  selected: ((options.filters.medium == agg.key) ? true : false)
                };
            });

          // A selected facet value can drop out of the aggregations when the
          // current combination yields no hits. Keep it listed (count 0) so it
          // can still be unchecked.
          ['artist', 'culture', 'department', 'medium'].forEach(key => {
            (options.filters[key] || []).forEach(value => {
              if (!filters[key].options[value])
                filters[key].options[value] = { count: 0, selected: true };
            });
          });

          const hasHits = results?.hits && results.hits.length > 0;
          const hasActiveFilters = Object.keys(options.filters || {}).length > 0;
          // Keep the filter panel mounted when a filter combination yields
          // nothing, otherwise the user has no way to widen the range again.
          const showFilters = !loading && (hasHits || hasActiveFilters);

          return (
            <>
              {showFilters && (
                <ConditionalWrap
                  condition={breakpoint.includes('desktop')}
                  wrap={children => <TwoColumn variant="wide-right">{children}</TwoColumn>}
                >

                  <CollectionFilters filters={filters} filterLimit={5} onChange={handleFilterChange} onRangeChange={handleRangeChange} />
                  {/* TwoColumn clones every child, so always pass a real element here. */}
                  {hasHits ? (
                  <div>
                    <SearchResultsGrid cardsPerRow={rowMapping[breakpoint]}>
                      {results.hits.map((result, index) => {
                        const imgSrc = (result.primary_image == -1) ? ImageNotFound : buildIIIFImageURL(result.id, result.primary_image, { size: 200, type: 'square' });

                        const constituent = result.constituents
                          ? result.constituents
                              .filter(c => c.role !== "Depicted individual")
                              .map(c => c.name)
                              .join('\n')
                          : '';

                        return (
                          <Link key={index} to={{ pathname: `object/${result.id}` }}>
                            <Card
                              title={result.title}
                              caption={constituent}
                              image={imgSrc}
                              variant="mini"
                            />
                          </Link>
                        );
                      })}
                      {(() => {
                        const cardsPerRow = rowMapping[breakpoint];
                        if (results.hits.length >= cardsPerRow) return null;
                        return Array.from({ length: cardsPerRow - results.hits.length }, (_, i) => (
                          <EmptyCell key={`filler-${i}`} variant="mini" />
                        ));
                      })()}
                    </SearchResultsGrid>
                    <PaginationContainer>
                      <Pagination
                        currentPage={results?.currentPage || 1}
                        pageCount={results?.pageCount || 1}
                        onChange={handlePageChange}
                      />
                    </PaginationContainer>
                  </div>
                  ) : (
                    <Box w="100%" pt="48px">
                      <Body textAlign="center">{noResultsText}</Body>
                    </Box>
                  )}
                </ConditionalWrap>
              )}
              {results?.hits?.length === 0 && !loading && !hasActiveFilters && (
                <Box w="100%" pt="48px">
                  <Body textAlign="center">{noResultsText}</Body>
                </Box>
              )}
              {loading && (
                <Box w="100%" pt="48px">
                  <Body textAlign="center">{loadingText}...</Body>
                </Box>
              )}
            </>
          );
        })()}
      </Container>
    );
});

export default CollectionSearch;
